/**
 * Home / Portal Demo — inbox notifications store.
 *
 * THROWAWAY DEMO CODE. Holds the demo inbox notifications (a failed workflow, a
 * materialize-eligible agent, an approval, and a memory review) with their
 * read / archived / resolved state in a resettable store, so the Cmd+K "Reset
 * demo" restores them to unread. The single human-in-the-loop *task* (an agent
 * asking a question) lives in `./fixtures` and renders on the Tasks tab.
 *
 * Delete with src/demo/home.
 */

import { useMemo } from 'react';
import { createDemoStore } from '@/demo/core/create-demo-store';
import { isDemoMode } from '@/demo/core/demo-mode';
import type {
  DemoMemoryEntry,
  DemoNotifData,
  InboxItem,
} from '@/pages/authenticated/inbox/components/data';
import { DEMO_WORKFLOW_ID } from '@/demo/workflow-recovery/fixtures';
import { HOME_DEMO_AGENT_ID } from './fixtures';

interface HomeNotif {
  id: string;
  itemKind: InboxItem['kind'];
  agentId: string | null;
  title: string;
  summary: string;
  /** Absolute timestamp (ms) — fixed at seed time so renders stay stable. */
  t: number;
  read: boolean;
  archived: boolean;
  priority: boolean;
  demo: DemoNotifData;
}

interface HomeInboxState {
  notifications: HomeNotif[];
}

function seedMemories(): DemoMemoryEntry[] {
  return [
    {
      id: 'm1',
      kind: 'new',
      decision: null,
      text: 'Apex Industrial Group bills on behalf of MRO Supply Co.',
    },
    {
      id: 'm2',
      kind: 'new',
      decision: null,
      text: 'Freight surcharges from Sentinel post to GL 5120.',
    },
    {
      id: 'm3',
      kind: 'new',
      decision: null,
      text: 'Brazos Valley Logistics invoices are Net 45.',
    },
    {
      id: 'm4',
      kind: 'existing',
      decision: null,
      text: 'MRO Supply Co. billing contact is Marie Hollis.',
    },
    {
      id: 'm5',
      kind: 'existing',
      decision: null,
      text: 'Pay Border States invoices on the 1st and the 15th.',
    },
    {
      id: 'm6',
      kind: 'existing',
      decision: null,
      text: 'Stale: Unistrut = part #U-1625 (supplier discontinued it).',
    },
    {
      id: 'm7',
      kind: 'existing',
      decision: null,
      text: 'Stale: route freight approvals to Dana (no longer here).',
    },
  ];
}

function buildSeed(): HomeInboxState {
  const now = Date.now();
  const at = (min: number) => now - min * 60_000;
  return {
    notifications: [
      {
        id: 'home-notif-workflow',
        itemKind: 'workflow-fail',
        agentId: null,
        title: 'Statement-email workflow failed for 23 runs',
        summary:
          'The "Email statement to customer" step failed for 23 of 412 runs — those customers have no billing email on file. The agent drafted a fix and backtested it against history (99.8% pass).',
        t: at(8),
        read: false,
        archived: false,
        priority: true,
        demo: {
          notifKind: 'workflow',
          workflowId: DEMO_WORKFLOW_ID,
          resolved: false,
        },
      },
      {
        id: 'home-notif-approval',
        itemKind: 'approval',
        agentId: null,
        title: 'Approve early payment — Apex Industrial ($9,604)',
        summary:
          'The AP agent scheduled invoice AIG-90412 to pay on the 2/10 discount date and needs your sign-off before releasing the ACH payment.',
        t: at(15),
        read: false,
        archived: false,
        priority: true,
        demo: {
          notifKind: 'approval',
          approvalDetail:
            'Pay $9,604 by Jul 1 (2/10 Net 30) — captures a 2% ($196) discount. Cash Flow Projection confirms it’s safe.',
          resolved: false,
        },
      },
      {
        id: 'home-notif-memory',
        itemKind: 'hitl',
        agentId: null,
        title: "Prune Avery's memory",
        summary:
          'Avery saved new mappings and flagged a couple of stale memories. Review what to keep and prune the rest in the agent’s memory.',
        t: at(70),
        read: false,
        archived: false,
        priority: true,
        demo: {
          notifKind: 'memory',
          memories: seedMemories(),
          resolved: false,
        },
      },
      {
        id: 'home-notif-materialize',
        itemKind: 'system',
        agentId: HOME_DEMO_AGENT_ID,
        title: 'Reconciliation agent is ready to become a workflow',
        summary:
          'This agent has run the same reconciliation 128 times with a 99% success rate — it’s eligible to materialize into a fast, governed workflow, with the agent kept on standby for recovery.',
        t: at(42),
        read: false,
        archived: false,
        priority: false,
        demo: {
          notifKind: 'materialize',
          agentId: HOME_DEMO_AGENT_ID,
          resolved: false,
        },
      },
    ],
  };
}

export const homeInboxStore = createDemoStore(buildSeed);

/** Unread (and unarchived) demo notification count — feeds the inbox badge. */
export function homeInboxUnreadCount(): number {
  return homeInboxStore
    .getState()
    .notifications.filter((n) => !n.read && !n.archived).length;
}

export function markHomeNotifRead(id: string): void {
  homeInboxStore.update((s) => {
    const n = s.notifications.find((x) => x.id === id);
    if (n) n.read = true;
  });
}

export function archiveHomeNotif(id: string): void {
  homeInboxStore.update((s) => {
    const n = s.notifications.find((x) => x.id === id);
    if (n) n.archived = true;
  });
}

export function resolveHomeApproval(id: string): void {
  homeInboxStore.update((s) => {
    const n = s.notifications.find((x) => x.id === id);
    if (n) n.demo.resolved = true;
  });
}

function toInboxItem(n: HomeNotif): InboxItem {
  return {
    id: n.id,
    kind: n.itemKind,
    status: n.archived ? 'archived' : 'open',
    read: n.read,
    priority: n.priority,
    agentId: n.agentId,
    t: n.t,
    title: n.title,
    summary: n.summary,
    resolved: n.demo.resolved,
    demo: n.demo,
  };
}

/** The demo inbox notifications as InboxItems; empty when demo mode is off. */
export function useHomeDemoInboxItems(): InboxItem[] {
  const notifications = homeInboxStore.useStore((s) => s.notifications);
  const demoOn = isDemoMode();
  return useMemo(
    () =>
      demoOn ? notifications.filter((n) => !n.archived).map(toInboxItem) : [],
    [notifications, demoOn],
  );
}
